import { Box, Flex, Heading, Skeleton, Spacer, Stack } from '@chakra-ui/react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { InvoiceDetailInformation } from '../InvoiceDetailInformation';
import { InvoiceStatusBadge } from '../InvoiceStatusBadge';
import { getInvoiceComputedStatus } from '../utils';
import { InvoiceDetailActionsMenu } from './InvoiceDetailActionsMenu';
import { InvoiceDetailDisbursementAndPaymentInKind } from './InvoiceDetailDisbursementAndPaymentInKind';
import { Breadcrumbs } from 'packages/common-frontend/components/Breadcrumbs';
import { truncateUuid } from 'packages/common-frontend/utils';
import { useGetInvoiceByIdQuery } from 'packages/invoices/requests/invoices.gql.generated';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

const RouteParamsStruct = z.object({
  invoiceId: z.string({ message: 'Missing URL parameter' }),
});

const InvoiceDetailSkeleton = ({ invoiceId }: { invoiceId: string }) => {
  return (
    <Box>
      <Breadcrumbs breadcrumbs={[{ label: 'Invoices', path: '/invoices' }]} />
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mt="24px"
        mb="40px"
      >
        <Heading display="inline-block">{`Invoice #${truncateUuid(
          invoiceId,
        )}`}</Heading>
      </Flex>
      <Skeleton h="600px" />
    </Box>
  );
};

export const InvoiceDetail = () => {
  const params = useParams();
  const { invoiceId } = RouteParamsStruct.parse(params);

  const { user } = useUserAuthOrThrow();

  const {
    data: invoiceData,
    loading: isInvoiceLoading,
  } = useGetInvoiceByIdQuery({
    variables: { invoiceId },
    fetchPolicy: 'no-cache',
  });

  if (!invoiceData || isInvoiceLoading) {
    return <InvoiceDetailSkeleton invoiceId={invoiceId} />;
  }

  const invoice = invoiceData.getInvoiceById;

  const computedStatus = getInvoiceComputedStatus({
    status: invoice.status,
    dueDate: invoice.dueDate,
    targetInvoiceDate: invoice.targetInvoiceDate ?? undefined,
  });

  // disbursements only apply to certain conditions
  // @dani todo: remove the loan type check once we have disbursements support for "facility" invoice types
  const showDisbursementsAndPaymentInKind =
    invoice.invoiceType === 'loan' &&
    invoice.paymentDestinationOrganizationId === user.organizationId;

  return (
    <Box>
      <Breadcrumbs breadcrumbs={[{ label: 'Invoices', path: '/invoices' }]} />
      <Flex alignItems="center" mt="24px" mb="40px">
        <Heading display="inline-block" mr="32px">{`Invoice #${truncateUuid(
          invoiceId,
        )}`}</Heading>
        <InvoiceStatusBadge computedStatus={computedStatus} />
        <Spacer />
        <InvoiceDetailActionsMenu invoice={invoice} />
      </Flex>
      <Stack gap="2em">
        <InvoiceDetailInformation invoice={invoice} />
        {showDisbursementsAndPaymentInKind && (
          <InvoiceDetailDisbursementAndPaymentInKind invoice={invoice} />
        )}
      </Stack>
    </Box>
  );
};
